import { Close, Description, Root, Title } from "@radix-ui/react-toast";
import { CloseIcon } from "@theme/assets";
import { alertIcons, alertStyles } from "@theme/themes/primitives/alert";
import { motion } from "framer-motion";
import { createElement } from "react";

import { Box, Flex, IconButton, Paragraph, ThemeWrapper, getThemeValue } from "@theme/components";

import { useMediaQuery } from "@theme/utils/hooks/useMediaQuery";

import type { ToastProps as ToastPrimitiveProps } from "@radix-ui/react-toast";
import type { RequireField } from "@theme/types/global";
import type { FC } from "react";

export interface ToastProps extends RequireField<ToastPrimitiveProps, "id"> {
  variant?: "error" | "success" | "disclaimer";
  description?: string;
  onExited?: () => void;
  visible?: boolean;
}

const MotionBox = motion(Box);

const mobileVariants = {
  hidden: {
    opacity: 0,
    y: 100,
    transition: { type: "spring", bounce: 0.1 },
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: { type: "spring", bounce: 0.1 },
  },
};

const desktopVariants = {
  hidden: {
    opacity: 0,
    x: 100,
    transition: { type: "spring", bounce: 0.1 },
  },
  visible: {
    opacity: 1,
    x: 0,
    transition: { type: "spring", bounce: 0.1 },
  },
};

export const Toast: FC<ToastProps> = ({
  variant = "success",
  title,
  description,
  visible = true,
  onExited,
  type = "foreground",
  ...props
}) => {
  const isMobile = useMediaQuery("sm", "max");
  const variants = isMobile ? mobileVariants : desktopVariants;

  return (
    <ThemeWrapper>
      <Root {...props} type={type} asChild>
        <MotionBox
          sx={alertStyles(variant, true).root}
          variants={variants}
          initial="hidden"
          animate={visible ? "visible" : "hidden"}
          layout
          transition={{
            layout: { type: "spring", bounce: 0.15, duration: 0.3 },
          }}
          onAnimationComplete={(def) => {
            if (def === "hidden" && onExited) {
              onExited();
            }
          }}
        >
          <Flex sx={alertStyles(variant, true).container}>
            <Flex sx={alertStyles(variant, true).icon}>{createElement(alertIcons[variant])}</Flex>
            <Flex sx={alertStyles(variant, true).content}>
              <Title asChild>
                <Paragraph variant="sansBody01Medium">{title}</Paragraph>
              </Title>
              {description && (
                <Description asChild>
                  <Paragraph variant="sansBody01Regular">{description}</Paragraph>
                </Description>
              )}
            </Flex>
            <Close asChild>
              <IconButton
                variant="iconSm"
                sx={{
                  // What can we fix these styles?
                  flex: "0 0 28px",
                  alignSelf: "start",
                  ml: "auto",
                  mr: "s100",
                  transform: (t) => `translateY(-${getThemeValue(t, "space.s100")})`,
                  svg: { color: "textPrimary" },
                }}
              >
                <CloseIcon />
              </IconButton>
            </Close>
          </Flex>
        </MotionBox>
      </Root>
    </ThemeWrapper>
  );
};
